/**
 * Exposes a hook to generate link paths throughout our app
 *
 * @type Linkable - string literal based on the exported function names of `@/util/link-generators`
 *
 * Can't find the Linkable you need? Add a new exported link generator in the link-generators util,
 * it will automatically show up as an option for `useExplorerLinkTo` or `useDbtCloudLinkTo`
 *
 */
import { useEffect } from 'react';
import { NavigateFunction, useLocation } from 'react-router-dom';

import { useCurrentPage } from '@/components/Page';
import {
  useAllowDbtCloudLinks,
  useDbtCloudAccount,
  useExplorePathPrefix,
} from '@/context/DbtCloudProvider';
import * as generators from '@/util/linkGenerators';
import * as dbtCloudGenerators from '@/util/linkGeneratorsDbtCloud';
import { GetParamType, LinkGenerator } from '@/util/linkTypes';

export type Linkable = keyof typeof generators;

type LinkGeneratorMap = {
  [key in Linkable]: LinkGenerator<GetParamType<(typeof generators)[key]>>;
};

const linkGenerators: LinkGeneratorMap = generators;

export const navigateNewTab = (
  url: string,
  navigate: NavigateFunction,
  enableExternalLinks: boolean,
) => {
  // explorer in snowflake does not support external links
  if (!enableExternalLinks) {
    navigate(url);
  } else {
    if (!url.startsWith('/explore')) {
      url = `/explore${url}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

/**
 * Generate a link within Explorer.
 */
export const useLinkToExplorer = <K extends Linkable>(
  item: K,
): LinkGenerator<GetParamType<(typeof generators)[K]>> => {
  const linkGenerator = linkGenerators[item];
  const { setLineageIngressPath } = useCurrentPage();
  const location = useLocation();
  const currentPath = location.pathname + location.search;

  const explorePrefix = useExplorePathPrefix();

  useEffect(() => {
    if (
      (item === 'projectLineage' || item === 'columnLineage') &&
      setLineageIngressPath &&
      !currentPath.includes('lineage')
    ) {
      setLineageIngressPath(currentPath);
    }
  }, [item, currentPath, setLineageIngressPath]);

  return (params: GetParamType<typeof linkGenerator>): string => {
    const path = linkGenerator({ ...params });
    return `${explorePrefix}${path}`;
  };
};

export type DbtCloudLinkable = keyof typeof dbtCloudGenerators;

type DbtCloudLinkGeneratorMap = {
  [key in DbtCloudLinkable]: LinkGenerator<
    GetParamType<(typeof dbtCloudGenerators)[key]>
  >;
};

const dbtCloudLinkGenerators: DbtCloudLinkGeneratorMap = dbtCloudGenerators;

const requiresDevLicense: DbtCloudLinkable[] = [
  'cloudIde',
  'insightsDbt',
  'insightsDWH',
  'canvasDbtCreate',
  'canvasDbtEdit',
  'canvasDWHCreate',
  'canvasDWHEdit',
  'serviceToken',
];

/**
 * Link to a dbt Cloud page. If this returns null, then do not
 * render the link. The link could be null if Explorer is embedded
 * in Snowpark, where we do not have access to dbt Cloud.
 */
export const useLinkToDbtCloud = <K extends DbtCloudLinkable>(
  item: K,
): LinkGenerator<GetParamType<(typeof dbtCloudGenerators)[K]>> | null => {
  const linkGenerator = dbtCloudLinkGenerators[item];
  const { hasDeveloperLicense, isAdmin } = useDbtCloudAccount();

  const enableDbtCloudLinks = useAllowDbtCloudLinks();
  if (!enableDbtCloudLinks) return null;

  if (!hasDeveloperLicense && requiresDevLicense.includes(item)) return null;
  if (item === 'serviceToken' && (!isAdmin || !hasDeveloperLicense)) return null;

  return (params: GetParamType<typeof linkGenerator>): string => {
    return linkGenerator({ ...params });
  };
};
